import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  ButtonBase,
  useTheme,
  Grid,
  type SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { toast } from "react-toastify";
import { MuiTelInputInfo } from "mui-tel-input";
import { AxiosError } from "axios";

import { tokens } from "../../../theme";
import apiService from "../../../service/Api";
import { AddCrewMemberDialog, SearchPilotDialog } from "../../crew-management/components";
import { AddCrewFormProps, RoleSkillTool } from "../../crew-management/types/add-crew-member";
import { UserProps } from "../../crew-management/types/search-pilot";

const BOOKING_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: "Funeral", value: "funeral" },
  { label: "Funeral Home", value: "funeral_home" },
  { label: "Church", value: "church" },
  { label: "Event", value: "event" },
  { label: "Conference", value: "conference" },
  { label: "Other", value: "other" },
];

interface ActionItem {
  label: string;
  icon: React.ReactNode;
  bgColor: string;
  hoverBg: string;
  onClick?: () => void;
}

export default function QuickActions() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [selectedBookingType, setSelectedBookingType] = useState<string | null>(null);

  const handleNewBookingContinue = () => {
    if (!selectedBookingType) return;
    setNewBookingOpen(false);
    navigate(`/bookings/add-v2/${selectedBookingType}`, {
      state: { serviceType: selectedBookingType },
    });
    setSelectedBookingType(null);
  };

  const [selectedCountry, setSelectedCountry] = useState<string | null>();
  const [selectedRoles, setSelectedRoles] = useState<RoleSkillTool[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<RoleSkillTool[]>([]);
  const [selectedTools, setSelectedTools] = useState<RoleSkillTool[]>([]);
  const [searchMemberOpen, setSearchMemberOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<UserProps | null>();
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState<AddCrewFormProps>({
    existingUser: false,
    user_id: null,
    first_name: "",
    last_name: "",
    email: "",
    username: "",
    password: "tilapia123",
    phone: "",
    role: "crew",
    organization: "",
    street_address: "",
    city: "",
    state: "",
    postal_code: "",
    residence_country: "",
    emergency_contact: "",
    emergency_contact_phone: "",
    date_of_birth: "1970-01-01",
    occupation: "",
    marital_status: "single",
    national_id: "",
    roles: [],
    skills: [],
    tools: [],
    preferences: "",
    experience_years: 0,
  });

  const resetForm = () => {
    setFormData({
      existingUser: false,
      user_id: null,
      first_name: "",
      last_name: "",
      email: "",
      username: "",
      password: "tilapia123",
      phone: "",
      role: "crew",
      organization: "",
      street_address: "",
      city: "",
      state: "",
      postal_code: "",
      residence_country: "",
      emergency_contact: "",
      emergency_contact_phone: "",
      date_of_birth: "",
      occupation: "",
      marital_status: "single",
      national_id: "",
      roles: [],
      skills: [],
      tools: [],
      preferences: "",
      experience_years: 0,
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleEmergencyContactChange = (
    value: string,
    info: MuiTelInputInfo,
  ) => {
    setFormData((prev: AddCrewFormProps) => ({
      ...prev,
      emergency_contact_phone: `+${info.countryCallingCode}${info.nationalNumber}`,
    }));
  };

  const handlePhoneChange = (value: string, info: MuiTelInputInfo) => {
    setFormData((prev: AddCrewFormProps) => ({
      ...prev,
      phone: `+${info.countryCallingCode}${info.nationalNumber}`,
    }));
  };

  const removeRole = (roleToRemove: number) => {
    setSelectedRoles(selectedRoles.filter((role) => role.id !== roleToRemove));
  };

  const removeSkill = (skillToRemove: number) => {
    setSelectedSkills(selectedSkills.filter((skill) => skill.id !== skillToRemove));
  };

  const removeTool = (toolToRemove: number) => {
    setSelectedTools(selectedTools.filter((tool) => tool.id !== toolToRemove));
  };

  const handleCountryChange = (country: string | null) => {
    setSelectedCountry(country);

    setFormData((prev: AddCrewFormProps) => ({
      ...prev,
      residence_country: country,
    }));
  };

  const handleRolesChange = (roles: RoleSkillTool[]) => {
    if (selectedRoles.find((role) => role.id === roles[roles.length - 1].id)) {
      toast.error(`The role ${roles[roles.length - 1].name} has already been selected`);
      return;
    }
    setSelectedRoles([...selectedRoles, roles[roles.length - 1]]);

    // eslint-disable-next-line prefer-const
    let roleIds: number[] = [];
    roles.forEach((role: RoleSkillTool) => {
      roleIds.push(role.id);
    });

    setFormData((prev: AddCrewFormProps) => ({
      ...prev,
      role: roles[0].name,
      roles: roleIds,
    }));
  };

  const handleSkillsChange = (skills: RoleSkillTool[]) => {
    if (selectedSkills.find((skill) => skill.id === skills[skills.length - 1].id)) {
      toast.error(`The skill ${skills[skills.length - 1].name} has already been selected`);
      return;
    }
    setSelectedSkills([...selectedSkills, skills[skills.length - 1]]);

    // eslint-disable-next-line prefer-const
    let skillIds: number[] = [];
    skills.forEach((skill: RoleSkillTool) => {
      skillIds.push(skill.id);
    });

    setFormData((prev: AddCrewFormProps) => ({ ...prev, skills: skillIds }));
  };

  const handleToolsChange = (tools: RoleSkillTool[]) => {
    if (selectedTools.find((tool) => tool.id === tools[tools.length - 1].id)) {
      toast.error(`The tool ${tools[tools.length - 1].name} has already been selected`);
      return;
    }
    setSelectedTools([...selectedTools, tools[tools.length - 1]]);

    // eslint-disable-next-line prefer-const
    let toolIds: number[] = [];
    tools.forEach((tool: RoleSkillTool) => {
      toolIds.push(tool.id);
    });

    setFormData((prev: AddCrewFormProps) => ({ ...prev, tools: toolIds }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const handleAddCrewMember = async () => {
    const {
      email,
      existingUser,
      date_of_birth,
      first_name,
      last_name,
      password,
      phone,
      roles,
      username,
    } = formData;

    if (!first_name.length) {
      toast.error("Please provide a first name for this pilot.");
      return;
    }
    if (!last_name.length) {
      toast.error("Please provide a last name for this pilot.");
      return;
    }
    if (!email.length) {
      toast.error("Please provide an email for this pilot.");
      return;
    }
    if (!username.length) {
      toast.error("Please provide a username for this pilot.");
      return;
    }
    if (!phone) {
      toast.error("Please provide a phone number for this pilot.");
      return;
    }
    if (!roles.length) {
      toast.error("Please select at least 1 role for this pilot.");
      return;
    }
    if (!password.length) {
      toast.error("Please provide a password for this pilot.");
      return;
    }
    if (!date_of_birth) {
      toast.error("Please provide a date of birth for this pilot.");
      return;
    }

    let payload = {};
    if (existingUser) {
      payload = {
        user_id: formData.user_id,
        roles: formData.roles,
        skills: formData.skills,
        tools: formData.tools,
        experience_years: formData.experience_years,
        bio: "Pixel Pilot",
      };
    } else {
      payload = {
        user: {
          email: email,
          username: username,
          first_name: first_name,
          last_name: last_name,
          password: password,
          user_type: "individual",
          phone_number: formData.phone,
          role: "crew",
          organization: formData.organization,
          street_address: formData.street_address,
          city: formData.city,
          state_province: formData.state,
          postal_code: formData.postal_code,
          residence_country: formData.residence_country,
          date_of_birth: formData.date_of_birth,
          occupation: formData.occupation,
          marital_status: formData.marital_status,
          national_id: formData.national_id,
          emergency_contact: formData.emergency_contact,
          emergency_contact_phone: formData.emergency_contact_phone,
          assignment: selectedRoles[0].name,
          service_preferences: formData.preferences,
        },
        roles: formData.roles,
        skills: formData.skills,
        tools: formData.tools,
        experience_years: formData.experience_years,
        bio: "Pixel Pilot",
      };
    }

    setFormLoading(true);

    try {
      await apiService.post("/api/crews/crew-members/full-create/", payload);

      toast.success("Crew member added successfully!");
      resetForm();
      setAddMemberOpen(false);
    } catch (error) {
      if (error instanceof AxiosError) {
        const detail = error?.response?.data?.errors?.user_id
          ? error?.response?.data?.errors?.user_id[0]
          : error?.response?.data?.detail || "Failed to add crew member.";
        toast.error(detail);
      } else {
        console.error("Add crew error:", error);
      }
    } finally {
      setFormLoading(false);
    }
  };

  const ACTIONS: ActionItem[] = [
    {
      label: "Dispatch Car",
      icon: <LocalShippingIcon fontSize="medium" />,
      bgColor: "#007bff",
      hoverBg: "#0069d9",
    },
    {
      label: "Add Crew",
      icon: <GroupAddIcon fontSize="medium" />,
      bgColor: "#d97706",
      hoverBg: "#b45309",
      onClick: () => setSearchMemberOpen(true),
    },
    {
      label: "New Booking",
      icon: <NoteAddIcon fontSize="medium" />,
      bgColor: "#785848",
      hoverBg: "#5c4337",
      onClick: () => setNewBookingOpen(true),
    },
    {
      label: "Assign Crew",
      icon: <AssignmentIndIcon fontSize="medium" />,
      bgColor: "#5c5cd6",
      hoverBg: "#4747c2",
      onClick: () => navigate("/assign-crew")
    },
  ];

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "24px",
          backgroundColor: colors.primary[900],
          width: "100%",
        }}
      >
        <Typography variant="h5" fontWeight={700} color={colors.primary[100]} sx={{ mb: 2.5 }}>
          Quick Actions
        </Typography>

        <Grid container spacing={2}>
          {ACTIONS.map((action, idx) => (
            <Grid size={{ xs: 6 }} key={idx}>
              <ButtonBase
                disableRipple
                onClick={action.onClick}
                sx={{
                  width: "100%",
                  height: "60px",
                  backgroundColor: action.bgColor,
                  color: "#ffffff",
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  transition: "background-color 0.2s ease-in-out, transform 0.1s ease-in-out",
                  "&:hover": {
                    backgroundColor: action.hoverBg,
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {action.icon}
                </Box>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8rem", textAlign: "center" }}>
                  {action.label}
                </Typography>
              </ButtonBase>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <SearchPilotDialog
        searchMemberOpen={searchMemberOpen}
        setSearchMemberOpen={setSearchMemberOpen}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        setAddMemberOpen={setAddMemberOpen}
      />

      <AddCrewMemberDialog
        addMemberOpen={addMemberOpen}
        formData={formData}
        user={searchResults}
        formLoading={formLoading}
        handleAddCrewMember={handleAddCrewMember}
        handleEmergencyContactChange={handleEmergencyContactChange}
        handleInputChange={handleInputChange}
        handlePhoneChange={handlePhoneChange}
        handleSelectChange={handleSelectChange}
        onCountryChange={handleCountryChange}
        onRolesChange={handleRolesChange}
        onSkillsChange={handleSkillsChange}
        onToolsChange={handleToolsChange}
        resetForm={resetForm}
        selectedCountry={selectedCountry}
        selectedRoles={selectedRoles}
        selectedSkills={selectedSkills}
        selectedTools={selectedTools}
        setAddMemberOpen={setAddMemberOpen}
        setFormData={setFormData}
      />

      <Dialog
        open={newBookingOpen}
        onClose={() => setNewBookingOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>New Booking</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a booking type to continue.
          </Typography>
          <ToggleButtonGroup
            value={selectedBookingType}
            exclusive
            onChange={(_, value) => value && setSelectedBookingType(value)}
            sx={{ flexWrap: "wrap", gap: 1 }}
          >
            {BOOKING_TYPE_OPTIONS.map((opt) => (
              <ToggleButton
                key={opt.value}
                value={opt.value}
                sx={{ borderRadius: "8px !important", textTransform: "none", flex: "1 1 45%" }}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewBookingOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!selectedBookingType}
            onClick={handleNewBookingContinue}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}